import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import {
  Menu, X, ArrowRight, Check, Home, Dumbbell, Zap, Shield,
  Heart, Flame, Clock, Users, Target, ChevronDown, Instagram,
  Facebook, Mail, MapPin, Plus, Minus, Phone, Loader2, Crown, Medal, Award
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* =========================================================
   NAV
========================================================= */
function Nav({ openContact }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Program", id: "program" },
    { label: "Why", id: "why" },
    { label: "Pricing", id: "pricing" },
    { label: "Classes", id: "classes" },
    { label: "Store", id: "store" },
    { label: "FAQ", id: "faq" },
  ];

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/95 backdrop-blur-md border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="container-max flex items-center justify-between py-4 px-6">
        <a
          href="#top"
          data-testid="logo-link"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center gap-3"
        >
          <img src="/logo.png" alt="8LIMBS Fitness" className="h-12 w-12 object-contain" />
          <div className="text-white font-display leading-none">
            <div className="text-lg font-bold tracking-wide">8LIMBS</div>
            <div className="text-[10px] tracking-[0.3em] text-red-500">FITNESS</div>
          </div>
        </a>

        <div className="hidden md:flex items-center gap-10 text-white">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="nav-link"
            >
              {l.label}
            </button>
          ))}
          <a
            href="https://instagram.com/8limbsfitness8"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram @8limbsfitness8"
            data-testid="nav-instagram"
            className="w-10 h-10 border border-white/30 flex items-center justify-center hover:bg-red-600 hover:border-red-600 transition-colors"
          >
            <Instagram className="w-4 h-4" />
          </a>
          <button
            data-testid="nav-cta-book"
            onClick={openContact}
            className="btn-primary"
            style={{ padding: "0.7rem 1.4rem", fontSize: "0.8rem" }}
          >
            Book Session <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-black border-t border-white/10 px-6 py-6 space-y-5" data-testid="mobile-menu">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="block text-white nav-link w-full text-left"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => { openContact(); setOpen(false); }}
            className="btn-primary w-full"
            data-testid="mobile-cta-book"
          >
            Book Session <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </nav>
  );
}

/* =========================================================
   HERO
========================================================= */
function Hero({ openContact }) {
  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden grain" data-testid="hero-section">
      {/* Video BG */}
      <video
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        poster="/training-poster.jpg"
        data-testid="hero-video"
      >
        <source src="/training.mp4" type="video/mp4" />
      </video>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(10,10,10,0.82) 0%, rgba(10,10,10,0.6) 40%, rgba(228,0,43,0.55) 100%)",
        }}
      />
      <div className="container-max w-full px-6 py-32 relative z-10">
        <div className="max-w-4xl">
          <div className="rise-in inline-flex items-center gap-2 px-4 py-2 border border-red-500/50 bg-black/40 text-red-400 text-xs font-display tracking-[0.3em] mb-8">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            MUAY THAI FITNESS · ONLINE & IN PERSON
          </div>

          <h1
            data-testid="hero-headline"
            className="rise-in delay-100 font-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[0.95] uppercase"
          >
            Train Like <br />
            A <span className="text-red-500">Fighter.</span> <br />
            Built For <span className="red-slash">Real People.</span>
          </h1>

          <p
            data-testid="hero-sub"
            className="rise-in delay-200 mt-8 text-white/80 text-lg md:text-xl max-w-2xl leading-relaxed"
          >
            Get in shape, build confidence, and feel better in your body with simple
            Muay&nbsp;Thai-style training — whether you train at home or with me in person.
          </p>

          <div className="rise-in delay-300 mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-online"
              onClick={() => document.getElementById("pricing").scrollIntoView({ behavior: "smooth" })}
              className="btn-primary"
            >
              Start Online Program <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-cta-inperson"
              onClick={openContact}
              className="btn-ghost"
            >
              Train In Person
            </button>
          </div>

          <div className="rise-in delay-400 mt-14 flex flex-wrap items-center gap-8 text-white/70 text-sm">
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-red-500" /> Beginner-friendly</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-red-500" /> No equipment</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-red-500" /> 20–40 min sessions</div>
          </div>
        </div>
      </div>

      <button
        onClick={() => document.getElementById("hook").scrollIntoView({ behavior: "smooth" })}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 animate-bounce"
        aria-label="Scroll down"
        data-testid="hero-scroll-down"
      >
        <ChevronDown className="w-8 h-8" />
      </button>
    </section>
  );
}

/* =========================================================
   TICKER
========================================================= */
function Ticker() {
  const items = ["STRONGER", "LEANER", "SHARPER", "FITTER", "TOUGHER", "FOCUSED", "CONFIDENT"];
  const doubled = [...items, ...items, ...items];
  return (
    <div className="ticker-container" data-testid="ticker">
      <div className="ticker">
        {doubled.map((w, i) => (
          <span key={i} className="ticker-item">
            {w} <span className="ticker-dot" />
          </span>
        ))}
      </div>
    </div>
  );
}

/* =========================================================
   RELATABLE HOOK (Section 2)
========================================================= */
function RelatableHook() {
  const feelings = ["Out of shape", "Low energy", "Stuck — not knowing where to start"];
  return (
    <section id="hook" className="section-pad bg-white" data-testid="hook-section">
      <div className="container-max grid md:grid-cols-2 gap-16 items-center">
        <div className="reveal">
          <div className="section-eyebrow">If this sounds familiar</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase leading-tight text-black">
            You're not <span className="text-red-600">alone.</span>
          </h2>
          <div className="divider-red mt-6" />
          <p className="mt-6 text-gray-700 text-lg leading-relaxed">
            Most people don't need a hardcore program. They need something simple that actually works.
          </p>
        </div>
        <div className="reveal space-y-4">
          {feelings.map((f, i) => (
            <div
              key={i}
              data-testid={`feeling-${i}`}
              className="card-light p-6 flex items-center gap-4"
            >
              <div className="w-10 h-10 bg-red-600 flex items-center justify-center text-white font-display font-bold text-lg shrink-0">
                {i + 1}
              </div>
              <div className="font-display text-lg md:text-xl text-black uppercase tracking-wide">{f}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   WHAT THIS IS (Section 3)
========================================================= */
function WhatThisIs() {
  return (
    <section className="section-pad bg-black text-white relative overflow-hidden grain" data-testid="what-section">
      <div className="container-max relative z-10 text-center">
        <div className="reveal">
          <div className="section-eyebrow">What This Is</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-tight max-w-4xl mx-auto">
            Built for <span className="text-red-500">everyday people.</span>
          </h2>
        </div>

        <div className="reveal mt-16 grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <div className="p-8 border border-white/15">
            <div className="text-red-500 text-xs font-display tracking-[0.25em] mb-3">NOT FOR</div>
            <ul className="space-y-3 font-display text-lg uppercase">
              <li className="opacity-60 line-through">Fighters</li>
              <li className="opacity-60 line-through">Athletes</li>
              <li className="opacity-60 line-through">People already in shape</li>
            </ul>
          </div>
          <div className="p-8 bg-red-600 border border-red-600">
            <div className="text-white/80 text-xs font-display tracking-[0.25em] mb-3">FOR YOU IF YOU WANT TO</div>
            <ul className="space-y-3 font-display text-lg uppercase">
              <li className="flex items-center gap-3"><Check className="w-5 h-5" /> Lose weight</li>
              <li className="flex items-center gap-3"><Check className="w-5 h-5" /> Get stronger</li>
              <li className="flex items-center gap-3"><Check className="w-5 h-5" /> Feel more confident</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   TWO WAYS TO TRAIN (Section 4)
========================================================= */
function TwoWaysToTrain({ openContact }) {
  const online = [
    "Full 8-week program",
    "Follow-along workouts",
    "Beginner-friendly",
    "20–40 min sessions",
  ];
  const inperson = [
    "1-on-1 or small group",
    "Technique + conditioning",
    "Real accountability",
    "Personalized guidance",
  ];
  return (
    <section id="program" className="section-pad bg-gray-50" data-testid="program-section">
      <div className="container-max">
        <div className="reveal text-center max-w-2xl mx-auto mb-16">
          <div className="section-eyebrow">Two Ways To Train</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase text-black leading-tight">
            Choose your <span className="text-red-600">path.</span>
          </h2>
          <div className="divider-red mt-6" />
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Online */}
          <div className="reveal card-light p-8 md:p-10 relative overflow-hidden" data-testid="online-card">
            <div className="absolute top-0 right-0 w-40 h-40 bg-red-600/5 rounded-full -translate-y-20 translate-x-20" />
            <div className="relative">
              <div className="w-14 h-14 bg-black text-red-500 flex items-center justify-center mb-6">
                <Home className="w-7 h-7" />
              </div>
              <div className="section-eyebrow mb-2" style={{ marginBottom: "0.25rem" }}>Online Training</div>
              <h3 className="font-display text-3xl md:text-4xl font-bold text-black uppercase">
                Train Anywhere, Anytime
              </h3>
              <p className="mt-4 text-gray-600">Built for home. No gym needed. No excuses.</p>

              <ul className="mt-8 space-y-3">
                {online.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-800">
                    <div className="w-5 h-5 bg-red-600 text-white flex items-center justify-center text-xs mt-0.5 shrink-0">
                      <Check className="w-3 h-3" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>

              <button
                data-testid="online-cta"
                onClick={() => document.getElementById("pricing").scrollIntoView({ behavior: "smooth" })}
                className="btn-dark mt-10 w-full sm:w-auto"
              >
                Start Online <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* In Person */}
          <div className="reveal card-dark p-8 md:p-10 text-white relative overflow-hidden" data-testid="inperson-card">
            <div className="absolute top-0 right-0 w-40 h-40 bg-red-600/10 rounded-full -translate-y-20 translate-x-20" />
            <div className="relative">
              <div className="w-14 h-14 bg-red-600 text-white flex items-center justify-center mb-6">
                <Dumbbell className="w-7 h-7" />
              </div>
              <div className="text-red-500 text-xs font-display tracking-[0.3em] uppercase mb-2">In-Person Training</div>
              <h3 className="font-display text-3xl md:text-4xl font-bold uppercase">
                Train With Me Directly
              </h3>
              <p className="mt-4 text-white/70">Hands-on coaching. Real feedback. Faster results.</p>

              <ul className="mt-8 space-y-3">
                {inperson.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/90">
                    <div className="w-5 h-5 bg-red-600 text-white flex items-center justify-center text-xs mt-0.5 shrink-0">
                      <Check className="w-3 h-3" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>

              <button
                data-testid="inperson-cta"
                onClick={openContact}
                className="btn-primary mt-10 w-full sm:w-auto"
              >
                Book A Session <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   REMOVE FEAR (Section 5)
========================================================= */
function RemoveFear() {
  const items = ['Experience', 'A gym membership', 'To be "fit" first'];
  return (
    <section className="section-pad bg-white" data-testid="remove-fear-section">
      <div className="container-max">
        <div className="reveal max-w-4xl mx-auto text-center">
          <div className="section-eyebrow">No Pressure</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase text-black leading-tight">
            You don't <span className="text-red-600">need</span>…
          </h2>
        </div>
        <div className="reveal mt-12 flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
          {items.map((item, i) => (
            <div key={i} className="px-6 py-4 border-2 border-gray-200 font-display uppercase tracking-wide text-gray-500 line-through">
              {item}
            </div>
          ))}
        </div>
        <div className="reveal mt-12 text-center">
          <p className="font-display text-3xl md:text-5xl uppercase text-black">
            You just need to <span className="text-red-600 italic">start.</span>
          </p>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   MEET THE COACHES
========================================================= */
function MeetCoach({ openContact }) {
  const coaches = [
    {
      name: "Jimmy",
      title: "Founder · Owner · Trainer",
      photo: "/fighter.jpg",
      bio: "Founder, Owner, and Trainer at 8LIMBS Fitness. Jimmy has worked with top fighters around the world — bringing that level of coaching home to the gym and to every student who walks through the door.",
      stats: [
        { label: "Role", value: "Founder" },
        { label: "Title", value: "Owner · Trainer" },
        { label: "Coached", value: "World-class" },
      ],
      badge: "Founder · Owner · Trainer at 8LIMBS",
    },
    {
      name: "Wes Petrovic",
      title: "Head Coach",
      photo: "/wes.jpg",
      bio: 'Head Coach at 8LIMBS — known in the gym simply as "Coach." Pad holder, technician, and the guy who keeps the whole crew accountable. Expect real intensity and the push you actually need to hit your goals.',
      stats: [
        { label: "Role", value: "Head Coach" },
        { label: "Known As", value: '"Coach"' },
        { label: "Specialty", value: "Pad Work" },
      ],
      badge: 'Head Coach · "Coach"',
    },
    {
      name: "Ben Clarke",
      title: "Muay Thai Coach",
      video: "/ben-clarke.mp4",
      videoPoster: "/ben-clarke-poster.jpg",
      bio: 'Known in the gym as "Button" — up-and-coming young talent and an amazing coach in his own right. Ben has trained under Jimmy and is now part of the 8LIMBS coaching team. Sharp pad holder, sharper technique — the real deal.',
      stats: [
        { label: "Known As", value: '"Button"' },
        { label: "Specialty", value: "Pad Work" },
        { label: "Status", value: "Rising" },
      ],
      badge: 'Muay Thai Coach · "Button"',
    },
  ];

  return (
    <section id="coach" className="section-pad bg-white" data-testid="coach-section">
      <div className="container-max">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <div className="section-eyebrow">Meet The Coaches</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase text-black leading-tight">
            Trained in the <span className="text-red-600">ring.</span> Built for you.
          </h2>
          <div className="divider-red mt-6" />
          <p className="mt-6 text-gray-600 text-lg">
            Championship-level coaching. Delivered simply — so you can actually stick with it.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {coaches.map((c, i) => (
            <div key={c.name} className="reveal" data-testid={`coach-card-${i}`}>
              <div className="relative overflow-hidden bg-black aspect-[4/5]">
                {c.video ? (
                  <video
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={c.videoPoster}
                    data-testid={`coach-video-${i}`}
                  >
                    <source src={c.video} type="video/mp4" />
                  </video>
                ) : c.photo ? (
                  <img
                    src={c.photo}
                    alt={c.name}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center relative"
                    style={{
                      background:
                        "linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 50%, #B00020 140%)",
                    }}
                  >
                    <div className="absolute inset-0 grain opacity-30" />
                    <div className="text-center px-6 relative">
                      <img
                        src="/logo.png"
                        alt=""
                        className="h-24 w-24 mx-auto object-contain opacity-80"
                      />
                      <div className="mt-6 font-display text-2xl font-bold uppercase tracking-widest text-white">
                        {c.title}
                      </div>
                      <div className="mt-2 text-[10px] font-display tracking-[0.3em] uppercase text-white/50">
                        Photo Coming Soon
                      </div>
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-red-500 text-[10px] font-display tracking-[0.3em] uppercase">
                    {c.badge}
                  </div>
                  <div className="font-display text-white text-3xl md:text-4xl font-bold uppercase mt-1 leading-tight">
                    {c.name}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="section-eyebrow" style={{ marginBottom: "0.25rem" }}>
                  {c.title}
                </div>
                <p className="text-gray-700 text-base leading-relaxed">{c.bio}</p>

                <div className="mt-5 grid grid-cols-3 gap-3">
                  {c.stats.map((s, j) => (
                    <div key={j} className="p-3 border-l-2 border-red-600">
                      <div className="font-display text-xl md:text-2xl font-bold text-black leading-none">
                        {s.value}
                      </div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="reveal text-center mt-12">
          <button onClick={openContact} data-testid="coach-cta" className="btn-dark">
            Train With Us <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   COMMUNITY (Group photo)
========================================================= */
function Community() {
  return (
    <section className="relative py-32 px-6 bg-black text-white overflow-hidden" data-testid="community-section">
      <div className="container-max relative z-10">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <div className="section-eyebrow">The 8LIMBS Family</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-tight">
            Family inside <br />the gym <span className="text-red-500">— and outside.</span>
          </h2>
          <div className="divider-red mt-6" />
          <p className="mt-6 text-white/70 text-lg leading-relaxed">
            More than a workout. We build confidence here — so you walk taller, prouder,
            and stronger every day. In the ring and out of it.
          </p>
        </div>

        <div className="reveal relative group">
          <div className="overflow-hidden border-4 border-red-600/20">
            <img
              src="/group.jpg"
              alt="8LIMBS Fitness family"
              className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="absolute -bottom-4 left-4 md:left-10 bg-red-600 text-white px-5 py-3 font-display uppercase tracking-[0.2em] text-xs">
            Real Family · Real Progress
          </div>
        </div>

        <div className="reveal mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="font-display text-4xl md:text-5xl font-bold text-red-500">Family</div>
            <div className="text-white/70 text-sm mt-1 uppercase tracking-wider">In & Out The Gym</div>
          </div>
          <div>
            <div className="font-display text-4xl md:text-5xl font-bold text-red-500">Walk</div>
            <div className="text-white/70 text-sm mt-1 uppercase tracking-wider">Taller · Prouder</div>
          </div>
          <div>
            <div className="font-display text-4xl md:text-5xl font-bold text-red-500">Build</div>
            <div className="text-white/70 text-sm mt-1 uppercase tracking-wider">Real Confidence</div>
          </div>
          <div>
            <div className="font-display text-4xl md:text-5xl font-bold text-red-500">Zero</div>
            <div className="text-white/70 text-sm mt-1 uppercase tracking-wider">Judgment</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   WHY THIS WORKS (Section 6)
========================================================= */
function WhyThisWorks() {
  const quit = ["Too complicated", "Too long", "Too extreme"];
  const works = ["Simple", "Structured", "Built for real life"];
  return (
    <section id="why" className="section-pad bg-black text-white relative grain" data-testid="why-section">
      <div className="container-max relative z-10">
        <div className="reveal max-w-2xl mx-auto text-center mb-16">
          <div className="section-eyebrow">Why This Works</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-tight">
            Most people <span className="text-red-500">quit.</span> <br />You won't.
          </h2>
          <div className="divider-red mt-6" />
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="reveal p-8 border border-white/15" data-testid="quit-card">
            <div className="text-white/50 text-xs font-display tracking-[0.3em] mb-4">MOST PROGRAMS</div>
            <h3 className="font-display text-2xl uppercase mb-6">Built to quit</h3>
            <ul className="space-y-4">
              {quit.map((x, i) => (
                <li key={i} className="flex items-center gap-3 text-white/60">
                  <X className="w-5 h-5 text-red-500" />
                  <span className="line-through">{x}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal p-8 border-2 border-red-600 bg-red-600/5" data-testid="works-card">
            <div className="text-red-500 text-xs font-display tracking-[0.3em] mb-4">THIS PROGRAM</div>
            <h3 className="font-display text-2xl uppercase mb-6">Built to stick</h3>
            <ul className="space-y-4">
              {works.map((x, i) => (
                <li key={i} className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-red-500" />
                  <span className="font-display text-lg uppercase">{x}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="reveal text-center mt-12 text-white/70 text-lg">
          So you actually <span className="text-white font-semibold">stick with it.</span>
        </p>
      </div>
    </section>
  );
}

/* =========================================================
   WHAT YOU'LL FEEL (Section 7)
========================================================= */
function WhatYoullFeel() {
  const items = [
    { icon: Zap, title: "More energy", desc: "Wake up sharper, move through your day lighter." },
    { icon: Heart, title: "Better stamina", desc: "Climb stairs, play with your kids, keep going longer." },
    { icon: Shield, title: "Increased confidence", desc: "You'll move differently. And you'll feel it." },
    { icon: Flame, title: "Your body changing", desc: "Leaner, stronger — not overnight, but real." },
  ];
  return (
    <section className="section-pad bg-gray-50" data-testid="feel-section">
      <div className="container-max">
        <div className="reveal text-center max-w-2xl mx-auto mb-16">
          <div className="section-eyebrow">What You'll Feel</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase text-black leading-tight">
            After a <span className="text-red-600">few weeks…</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="reveal card-light p-6 hover-lift" data-testid={`feel-card-${i}`}>
                <div className="w-12 h-12 bg-red-600 text-white flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-display text-xl font-bold uppercase text-black">{it.title}</h3>
                <p className="mt-3 text-gray-600 text-sm leading-relaxed">{it.desc}</p>
              </div>
            );
          })}
        </div>
        <p className="reveal text-center mt-12 text-gray-500 italic">Not overnight — but real progress.</p>
      </div>
    </section>
  );
}

/* =========================================================
   FOR NORMAL PEOPLE (Section 8)
========================================================= */
function ForNormalPeople() {
  const lines = [
    "You haven't trained in a while",
    "You get tired easily",
    "You don't enjoy the gym",
    "You want something different",
  ];
  return (
    <section className="relative py-32 px-6 text-white overflow-hidden" data-testid="normal-section">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.9) 100%), url('https://images.unsplash.com/photo-1517838277536-f5f99be501cd?auto=format&fit=crop&w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="container-max relative z-10">
        <div className="reveal max-w-3xl">
          <div className="section-eyebrow">This Is For You</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-tight">
            If you're a <span className="text-red-500">normal person</span> who wants to change something.
          </h2>
          <div className="divider-red mt-6" />
        </div>
        <div className="reveal mt-12 grid sm:grid-cols-2 gap-4 max-w-3xl">
          {lines.map((l, i) => (
            <div key={i} className="flex items-start gap-4 p-5 bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="w-8 h-8 bg-red-600 text-white flex items-center justify-center shrink-0 font-display font-bold">
                ✓
              </div>
              <div className="font-body text-white/90">{l}</div>
            </div>
          ))}
        </div>
        <p className="reveal mt-10 font-display text-2xl md:text-3xl text-red-500 uppercase">
          You don't need to be in shape to start.
        </p>
      </div>
    </section>
  );
}

/* =========================================================
   REAL TALK (Section 9) + RESULTS VISION (Section 10)
========================================================= */
function RealTalk() {
  return (
    <section className="section-pad bg-white" data-testid="realtalk-section">
      <div className="container-max grid md:grid-cols-2 gap-16 items-center">
        <div className="reveal">
          <div className="section-eyebrow">Real Talk</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase text-black leading-tight">
            Some days will <span className="text-red-600">feel hard.</span>
          </h2>
          <div className="divider-red mt-6" />
          <p className="mt-6 text-gray-700 text-lg leading-relaxed">
            Some days you won't feel motivated. That's normal.
          </p>
          <p className="mt-4 text-black font-semibold text-xl">
            This program is built to keep you going anyway.
          </p>
        </div>
        <div className="reveal">
          <div
            className="aspect-[4/5] bg-black relative overflow-hidden"
            style={{
              backgroundImage:
                "linear-gradient(0deg, rgba(228,0,43,0.15), transparent 60%), url('https://images.unsplash.com/photo-1517438322307-e67111335449?auto=format&fit=crop&w=900&q=80')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/90 to-transparent">
              <p className="font-display text-white text-2xl uppercase leading-tight">
                Show up <span className="text-red-500">anyway.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultsVision() {
  const imagines = [
    "Feeling comfortable in your body again",
    "Having energy throughout the day",
    "Actually enjoying your workouts",
  ];
  return (
    <section className="section-pad bg-red-600 text-white relative overflow-hidden" data-testid="vision-section">
      <div className="absolute inset-0 grain" />
      <div className="container-max relative z-10 text-center">
        <div className="reveal">
          <div className="text-white/80 text-xs font-display tracking-[0.3em] uppercase mb-4">Imagine</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-tight max-w-3xl mx-auto">
            That's what this is about.
          </h2>
        </div>
        <div className="reveal mt-14 space-y-4 max-w-2xl mx-auto">
          {imagines.map((x, i) => (
            <div
              key={i}
              className="flex items-center gap-4 bg-black/20 border border-white/20 p-5 text-left backdrop-blur-sm"
            >
              <div className="w-10 h-10 bg-white text-red-600 flex items-center justify-center font-display font-bold shrink-0">
                {i + 1}
              </div>
              <div className="font-display text-lg md:text-xl uppercase tracking-wide">{x}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   PRICING (3-tier subscription plans + Stripe Checkout)
========================================================= */
function Pricing({ openContact }) {
  const [loadingKey, setLoadingKey] = useState(null);
  const [err, setErr] = useState("");

  const plans = [
    {
      key: "essentials",
      name: "Essentials",
      price: 79,
      Icon: Medal,
      accent: "Bronze",
      tagline: "Start strong. Stay consistent.",
      features: [
        "Structured training program",
        "Monthly updates",
        "Basic support",
      ],
      cardClass: "card-light",
      textClass: "text-black",
      muteClass: "text-gray-600",
      btnClass: "btn-dark",
      highlight: false,
    },
    {
      key: "coaching",
      name: "Coaching",
      price: 129,
      Icon: Award,
      accent: "Most Popular",
      tagline: "Personalized. Focused. Effective.",
      features: [
        "Custom program tailored to you",
        "Bi-weekly check-ins",
        "Form feedback + adjustments",
        "Direct messaging support",
      ],
      cardClass: "card-dark",
      textClass: "text-white",
      muteClass: "text-white/70",
      btnClass: "btn-primary",
      highlight: true,
    },
    {
      key: "elite",
      name: "Elite",
      price: 199,
      Icon: Crown,
      accent: "Gold",
      tagline: "Everything. No guesswork.",
      features: [
        "Fully customized training + nutrition",
        "Weekly check-ins",
        "Priority messaging access",
        "Video breakdowns & technique coaching",
      ],
      cardClass: "card-light",
      textClass: "text-black",
      muteClass: "text-gray-600",
      btnClass: "btn-dark",
      highlight: false,
    },
  ];

  const subscribe = async (planKey) => {
    setErr("");
    setLoadingKey(planKey);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        plan_key: planKey,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url; // Redirect to Stripe
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Unable to start checkout. Please try again."
      );
      setLoadingKey(null);
    }
  };

  return (
    <section id="pricing" className="section-pad bg-white" data-testid="pricing-section">
      <div className="container-max">
        <div className="reveal text-center max-w-2xl mx-auto mb-4">
          <div className="section-eyebrow">Online Coaching</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase text-black leading-tight">
            Built for <span className="text-red-600">real results.</span>
          </h2>
          <div className="divider-red mt-6" />
          <p className="mt-6 text-gray-600 text-lg">
            No guesswork. No wasted time. Pick your level — cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto mt-14 items-stretch">
          {plans.map((p) => {
            const Icon = p.Icon;
            return (
              <div
                key={p.key}
                data-testid={`plan-${p.key}`}
                className={`reveal ${p.cardClass} p-8 md:p-9 relative flex flex-col ${
                  p.highlight ? "md:-translate-y-4 md:shadow-2xl" : ""
                }`}
              >
                {p.highlight && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-red-600 text-white text-[10px] font-display tracking-[0.3em] uppercase px-4 py-2">
                    {p.accent}
                  </div>
                )}
                {!p.highlight && (
                  <div className={`text-xs font-display tracking-[0.3em] uppercase ${p.muteClass}`}>
                    {p.accent}
                  </div>
                )}

                <div className="flex items-center gap-3 mt-4">
                  <div className={`w-11 h-11 flex items-center justify-center ${p.highlight ? "bg-red-600 text-white" : "bg-black text-red-500"}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={`font-display text-2xl md:text-3xl font-bold uppercase ${p.textClass}`}>
                    {p.name}
                  </h3>
                </div>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className={`font-display text-5xl md:text-6xl font-bold ${p.textClass}`}>
                    ${p.price}
                  </span>
                  <span className={`${p.muteClass} font-display tracking-wider uppercase text-sm`}>/mo</span>
                </div>
                <p className={`mt-2 ${p.muteClass} italic`}>{p.tagline}</p>

                <ul className="mt-7 space-y-3 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-3 ${p.highlight ? "text-white/90" : "text-gray-800"}`}>
                      <div className={`w-5 h-5 ${p.highlight ? "bg-red-500" : "bg-red-600"} text-white flex items-center justify-center shrink-0 mt-0.5`}>
                        <Check className="w-3 h-3" />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`subscribe-${p.key}`}
                  onClick={() => subscribe(p.key)}
                  disabled={loadingKey === p.key}
                  className={`${p.btnClass} mt-8 w-full`}
                >
                  {loadingKey === p.key ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Redirecting…
                    </>
                  ) : (
                    <>
                      Subscribe <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                <p className={`mt-3 text-center text-xs ${p.muteClass}`}>
                  Secure checkout via Stripe
                </p>
              </div>
            );
          })}
        </div>

        {err && (
          <div className="text-red-600 text-center mt-6" data-testid="pricing-error">
            {err}
          </div>
        )}

        <p className="mt-10 text-center text-gray-500 italic">Prefer face-to-face? Join a group class below.</p>
      </div>
    </section>
  );
}

/* =========================================================
   FIGHT WEAR STORE
========================================================= */
function Store({ openContact }) {
  const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
  // Track selected size per product
  const [sizes, setSizes] = useState({});

  const setSize = (id, sz) => setSizes((s) => ({ ...s, [id]: sz }));

  const products = [
    {
      id: "classic-tee",
      name: "8LIMBS Classic Tee",
      type: "Shirt",
      price: 80,
      colors: "Black",
      bg: "linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)",
      accent: "text-red-500",
    },
    {
      id: "fighter-tee",
      name: "Train Like A Fighter Tee",
      type: "Shirt",
      price: 80,
      colors: "White",
      bg: "linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)",
      accent: "text-red-600",
      light: true,
    },
    {
      id: "logo-tank",
      name: "8LIMBS Logo Tank",
      type: "Tank",
      price: 80,
      colors: "Black / Red",
      bg: "linear-gradient(135deg, #0A0A0A 0%, #B00020 130%)",
      accent: "text-white",
    },
    {
      id: "pro-shorts",
      name: "Pro Muay Thai Shorts",
      type: "Shorts",
      price: 80,
      colors: "Black / Red",
      photo: "/gear-hero.jpg",
      photoFocus: "center 75%",
      bg: "#0A0A0A",
      accent: "text-white",
      featured: true,
    },
    {
      id: "long-sleeve",
      name: "8LIMBS Performance Long Sleeve",
      type: "Long Sleeve",
      price: 80,
      colors: "Black / Red",
      photo: "/gear-hero.jpg",
      photoFocus: "center 25%",
      bg: "#0A0A0A",
      accent: "text-white",
      featured: true,
    },
    {
      id: "logo-hoodie",
      name: "8LIMBS Logo Hoodie",
      type: "Hoodie",
      price: 80,
      colors: "Black",
      bg: "linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%)",
      accent: "text-red-500",
    },
  ];

  const orderProduct = (p) => {
    const sz = sizes[p.id] || "(no size selected — please ask me)";
    openContact(
      `I'd like to order: ${p.name} — $${p.price}\nSize: ${sz}\nColour: ${p.colors}\n\nCan you confirm availability and pickup/shipping options?`
    );
  };

  const orderBundle = () => {
    const tee = sizes["bundle-tee"] || "?";
    const shorts = sizes["bundle-shorts"] || "?";
    const hoodie = sizes["bundle-hoodie"] || "?";
    openContact(
      `I'd like to order the FULL KIT BUNDLE — $210 (save $30)\n• Tee size: ${tee}\n• Shorts size: ${shorts}\n• Hoodie size: ${hoodie}\n\nCan you confirm availability and pickup/shipping options?`
    );
  };

  return (
    <section id="store" className="section-pad bg-white relative" data-testid="store-section">
      <div className="container-max">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <div className="section-eyebrow">Fight Wear</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase text-black leading-tight">
            Gear up. <span className="text-red-600">Train hard.</span>
          </h2>
          <div className="divider-red mt-6" />
          <p className="mt-6 text-gray-600 text-lg">
            Built for the gym. Made to last. Worn by the 8LIMBS crew.
          </p>
        </div>

        {/* Hero feature shot */}
        <div className="reveal grid md:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto mb-16 items-center" data-testid="store-hero">
          <div className="relative overflow-hidden bg-black aspect-[4/5]">
            <img
              src="/gear-hero.jpg"
              alt="8LIMBS Fitness branded shirt and Muay Thai shorts"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              loading="lazy"
            />
            <div className="absolute top-4 left-4 bg-red-600 text-white text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5">
              Official Gear
            </div>
          </div>
          <div>
            <div className="section-eyebrow">Wear it. Earn it.</div>
            <h3 className="font-display text-3xl md:text-4xl font-bold uppercase text-black leading-tight mt-2">
              Same gear. <span className="text-red-600">Same crew.</span>
            </h3>
            <div className="divider-red mt-5" />
            <p className="mt-6 text-gray-700 text-lg leading-relaxed">
              Real fight wear. Worn by everyone in the gym — from first-timers to fighters.
              Black & red, built tough, designed to last round after round.
            </p>
            <ul className="mt-6 space-y-2 text-gray-800">
              <li className="flex items-center gap-3"><Check className="w-5 h-5 text-red-600" /> Performance fabric — moves with you</li>
              <li className="flex items-center gap-3"><Check className="w-5 h-5 text-red-600" /> Embroidered 8LIMBS logo</li>
              <li className="flex items-center gap-3"><Check className="w-5 h-5 text-red-600" /> Sizes XS – XXL · Local pickup in BC</li>
            </ul>
          </div>
        </div>

        {/* FULL KIT BUNDLE */}
        <div className="reveal mb-16 max-w-6xl mx-auto bg-black text-white relative overflow-hidden" data-testid="bundle-card">
          <div className="absolute inset-0 grain opacity-30" />
          <div className="relative grid md:grid-cols-5 gap-0">
            {/* Image panel */}
            <div className="md:col-span-2 relative overflow-hidden min-h-[280px] md:min-h-0">
              <img
                src="/gear-hero.jpg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black" />
              <div className="absolute top-5 left-5 bg-red-600 text-white text-[10px] font-display tracking-[0.3em] uppercase px-3 py-1.5">
                Best Value
              </div>
            </div>

            {/* Bundle content */}
            <div className="md:col-span-3 p-8 md:p-10">
              <div className="text-red-500 text-xs font-display tracking-[0.3em] uppercase">Full Kit Bundle</div>
              <h3 className="font-display text-3xl md:text-4xl font-bold uppercase mt-2 leading-tight">
                Tee + Shorts + <span className="text-red-500">Hoodie</span>
              </h3>

              <div className="flex items-baseline gap-3 mt-5">
                <span className="font-display text-5xl md:text-6xl font-bold">$210</span>
                <span className="text-white/50 line-through text-xl font-display">$240</span>
                <span className="bg-red-600 text-white text-xs font-display tracking-widest px-2 py-1 uppercase">Save $30</span>
              </div>

              <p className="mt-3 text-white/70">Pick your sizes for each piece. We'll handle the rest.</p>

              <div className="mt-6 grid grid-cols-3 gap-3" data-testid="bundle-size-pickers">
                {[
                  { key: "bundle-tee", label: "Tee" },
                  { key: "bundle-shorts", label: "Shorts" },
                  { key: "bundle-hoodie", label: "Hoodie" },
                ].map((item) => (
                  <div key={item.key}>
                    <label className="text-[10px] font-display tracking-[0.25em] text-white/60 uppercase block mb-1.5">
                      {item.label} Size
                    </label>
                    <select
                      value={sizes[item.key] || ""}
                      onChange={(e) => setSize(item.key, e.target.value)}
                      data-testid={`size-${item.key}`}
                      className="w-full bg-white/5 border border-white/20 text-white px-3 py-2.5 font-display tracking-wider uppercase text-sm focus:border-red-500 focus:outline-none"
                    >
                      <option value="">Select</option>
                      {SIZES.map((s) => (
                        <option key={s} value={s} className="bg-black">{s}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <button
                onClick={orderBundle}
                className="btn-primary mt-6 w-full md:w-auto"
                data-testid="bundle-order-cta"
              >
                Order Full Kit <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`product-${p.id}`}
              className="group relative card-light p-0 overflow-hidden flex flex-col"
            >
              {p.featured && (
                <div className="absolute top-4 left-4 z-10 bg-red-600 text-white text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1">
                  Best Seller
                </div>
              )}

              {/* Visual area */}
              <div
                className="aspect-[4/5] relative flex items-center justify-center overflow-hidden"
                style={{ background: p.bg }}
              >
                {p.photo ? (
                  <img
                    src={p.photo}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ objectPosition: p.photoFocus || "center" }}
                    loading="lazy"
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 grain opacity-30" />
                    <div className="text-center px-6 transition-transform duration-700 group-hover:scale-105">
                      <img
                        src="/logo.png"
                        alt=""
                        className="h-20 w-20 md:h-24 md:w-24 mx-auto object-contain"
                        style={p.light ? { filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.15))" } : {}}
                      />
                      <div
                        className={`mt-5 font-display text-2xl md:text-3xl font-bold uppercase tracking-wider leading-tight ${p.accent}`}
                      >
                        {p.type}
                      </div>
                      <div className={`mt-1 text-[10px] font-display tracking-[0.3em] uppercase ${p.light ? "text-gray-600" : "text-white/60"}`}>
                        Photo Coming Soon
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Info */}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-red-600 text-[10px] font-display tracking-[0.3em] uppercase">
                      {p.type}
                    </div>
                    <h3 className="font-display text-lg font-bold uppercase text-black mt-1 leading-tight">
                      {p.name}
                    </h3>
                  </div>
                  <div className="font-display text-2xl font-bold text-black shrink-0">
                    ${p.price}
                  </div>
                </div>
                <p className="mt-2 text-gray-500 text-sm">{p.colors}</p>

                {/* Size picker */}
                <div className="mt-4">
                  <label className="text-[10px] font-display tracking-[0.25em] text-gray-500 uppercase block mb-1.5">
                    Size
                  </label>
                  <div className="flex flex-wrap gap-1.5" data-testid={`sizes-${p.id}`}>
                    {SIZES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSize(p.id, s)}
                        data-testid={`size-${p.id}-${s}`}
                        className={`min-w-[40px] px-2 py-1.5 text-xs font-display tracking-wider border transition-all ${
                          sizes[p.id] === s
                            ? "bg-red-600 border-red-600 text-white"
                            : "border-gray-300 text-gray-700 hover:border-red-600 hover:text-red-600"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => orderProduct(p)}
                  data-testid={`order-${p.id}`}
                  className="btn-dark mt-5 w-full"
                  style={{ padding: "0.85rem 1rem", fontSize: "0.85rem" }}
                >
                  DM To Order <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="reveal mt-12 max-w-3xl mx-auto bg-black text-white p-8 text-center" data-testid="store-info-strip">
          <div className="text-red-500 text-xs font-display tracking-[0.3em] uppercase mb-3">How it works</div>
          <p className="text-white/80 leading-relaxed">
            All gear is made to order. Pick your size, click <strong>DM To Order</strong> — I'll confirm availability and pickup or shipping. Local pickup available in Cobble Hill & Duncan, BC.
          </p>
          <a
            href="https://instagram.com/8limbsfitness8"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mt-6 inline-flex"
            data-testid="store-instagram-cta"
          >
            <Instagram className="w-4 h-4" /> Follow on Insta @8limbsfitness8
          </a>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   GROUP CLASSES (In-Person Schedule + Locations)
========================================================= */
function GroupClasses({ openContact }) {
  const classes = [
    {
      day: "Wednesday",
      location: "Cobble Hill, BC",
      address: "Cobble Hill, Vancouver Island",
      Icon: Dumbbell,
    },
    {
      day: "Sunday",
      location: "Duncan, BC",
      address: "4970 Pokey Rd, Duncan, BC",
      Icon: Dumbbell,
    },
  ];

  return (
    <section id="classes" className="section-pad bg-black text-white relative overflow-hidden grain" data-testid="group-classes-section">
      <div className="container-max relative z-10">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <div className="section-eyebrow">Group Classes · Vancouver Island</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase leading-tight">
            Train with the <span className="text-red-500">crew.</span>
          </h2>
          <div className="divider-red mt-6" />
          <p className="mt-6 text-white/70 text-lg">
            Two weekly sessions. Real energy. No judgment — just people showing up to get better.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {classes.map((c, i) => {
            const Icon = c.Icon;
            return (
              <div
                key={i}
                className="reveal p-8 border border-white/10 bg-white/5 backdrop-blur-sm hover-lift"
                data-testid={`class-${c.day.toLowerCase()}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 bg-red-600 text-white flex items-center justify-center">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="text-red-500 text-[10px] font-display tracking-[0.3em] uppercase">
                    Weekly
                  </div>
                </div>
                <div className="mt-5 font-display text-3xl md:text-4xl font-bold uppercase tracking-tight">
                  {c.day}
                </div>
                <div className="mt-1 text-xl text-white/90 font-display uppercase tracking-wide">
                  {c.location}
                </div>
                <div className="mt-4 flex items-start gap-2 text-white/70">
                  <MapPin className="w-4 h-4 text-red-500 mt-1 shrink-0" />
                  <span>{c.address}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pricing strip */}
        <div className="reveal mt-10 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div className="p-8 border-2 border-red-600 bg-red-600/5 text-center" data-testid="class-pricing-monthly">
            <div className="text-red-500 text-xs font-display tracking-[0.3em] uppercase mb-3">Unlimited Monthly</div>
            <div className="flex items-baseline justify-center gap-2">
              <span className="font-display text-6xl font-bold">$190</span>
              <span className="text-white/70 font-display tracking-widest uppercase text-sm">/mo</span>
            </div>
            <p className="mt-3 text-white/70">Both weekly classes · Best value for regulars</p>
          </div>
          <div className="p-8 border border-white/15 text-center" data-testid="class-pricing-drop-in">
            <div className="text-white/50 text-xs font-display tracking-[0.3em] uppercase mb-3">Drop-In</div>
            <div className="flex items-baseline justify-center gap-2">
              <span className="font-display text-6xl font-bold">$30</span>
              <span className="text-white/70 font-display tracking-widest uppercase text-sm">/session</span>
            </div>
            <p className="mt-3 text-white/70">Try a class · No commitment</p>
          </div>
        </div>

        {/* 1-on-1 Featured banner */}
        <div className="reveal mt-10 max-w-4xl mx-auto bg-gradient-to-br from-red-600 to-red-700 text-white relative overflow-hidden" data-testid="one-on-one-card">
          <div className="absolute inset-0 grain opacity-30" />
          <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
          <div className="relative p-8 md:p-10 grid md:grid-cols-5 gap-6 items-center">
            <div className="md:col-span-3">
              <div className="inline-flex items-center gap-2 bg-black/30 border border-white/30 px-3 py-1.5 mb-4">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-[10px] font-display tracking-[0.3em] uppercase">First Session On Us</span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-bold uppercase leading-tight">
                1-on-1 Training <br />
                <span className="text-black">Bag Work · Pad Work</span>
              </h3>
              <p className="mt-4 text-white/90 leading-relaxed">
                Want personal attention? Book a private session — full bag work,
                pad work, technique, and real feedback. <strong>Your first session is on us.</strong>
              </p>
            </div>
            <div className="md:col-span-2 text-center md:text-right">
              <div className="text-white/70 text-xs font-display tracking-[0.3em] uppercase">Per Hour</div>
              <div className="flex items-baseline justify-center md:justify-end gap-2 mt-1">
                <span className="font-display text-6xl md:text-7xl font-bold">$80</span>
                <span className="text-white/70 font-display tracking-widest uppercase text-sm">/hr</span>
              </div>
              <div className="mt-2 inline-flex items-center gap-2 bg-black text-white px-3 py-1.5 font-display text-xs tracking-[0.2em] uppercase">
                ★ First Session Free
              </div>
              <button
                onClick={() => openContact("I'd like to claim my FREE first 1-on-1 training session (bag work + pad work — $80/hr after that). When are you available?")}
                className="btn-dark mt-5 w-full md:w-auto"
                data-testid="one-on-one-cta"
              >
                Claim Free Session <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="reveal mt-10 text-center">
          <button onClick={openContact} className="btn-primary" data-testid="classes-book-cta">
            Book A Class <ArrowRight className="w-4 h-4" />
          </button>
          <p className="mt-4 text-white/50 text-sm">
            Limited spots. Message to reserve your first session.
          </p>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   FINAL CTA (Section 12 + 13)
========================================================= */
function FinalCTA({ openContact }) {
  return (
    <section className="relative py-32 px-6 text-white overflow-hidden" data-testid="final-cta-section">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(10,10,10,0.85), rgba(228,0,43,0.7)), url('https://images.unsplash.com/photo-1571019613914-85f342c6a11e?auto=format&fit=crop&w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="container-max relative z-10 text-center max-w-3xl mx-auto">
        <div className="reveal">
          <p className="font-display text-xl md:text-2xl uppercase tracking-[0.2em] text-white/80 mb-6">
            You don't need the perfect time.
          </p>
          <h2 className="font-display text-5xl md:text-7xl font-bold uppercase leading-[0.95]">
            You just need <br/> to <span className="text-red-400">start.</span>
          </h2>
        </div>
        <div className="reveal mt-12 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            data-testid="final-cta-online"
            onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-primary"
          >
            Start Online Program <ArrowRight className="w-4 h-4" />
          </button>
          <button
            data-testid="final-cta-inperson"
            onClick={openContact}
            className="btn-ghost"
          >
            Book In-Person Training
          </button>
        </div>

        <div className="reveal mt-20 pt-12 border-t border-white/20 max-w-xl mx-auto">
          <p className="font-display text-2xl md:text-3xl uppercase leading-tight">
            Train like a <span className="text-red-400">fighter.</span> <br />
            Live like a <span className="text-red-400">normal person.</span>
          </p>
          <p className="mt-4 text-white/70 font-display tracking-[0.3em] text-sm uppercase">
            Simple · Real · Effective
          </p>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   FAQ (Section 14)
========================================================= */
function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);
  const items = [
    { q: "Do I need experience?", a: "No. This is beginner-friendly — designed for people who have never trained Muay Thai or stepped in a gym before." },
    { q: "What if I'm out of shape?", a: "That's exactly who this is for. The program meets you where you are and builds you up at a pace you can stick with." },
    { q: "How long are workouts?", a: "20–40 minutes. Short enough to fit into real life, long enough to make real change." },
    { q: "What if I miss days?", a: "Just continue. No restarting. Life happens — the program is built to keep you moving forward regardless." },
    { q: "Do I need equipment?", a: "No. Everything is bodyweight-based and can be done in a small space at home." },
  ];
  return (
    <section id="faq" className="section-pad bg-gray-50" data-testid="faq-section">
      <div className="container-max max-w-3xl">
        <div className="reveal text-center mb-14">
          <div className="section-eyebrow">Frequently Asked</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase text-black">
            Got questions?
          </h2>
          <div className="divider-red mt-6" />
        </div>
        <div className="reveal">
          {items.map((it, i) => (
            <div
              key={i}
              className={`faq-item ${openIdx === i ? "open" : ""}`}
              data-testid={`faq-item-${i}`}
            >
              <div className="faq-q" onClick={() => setOpenIdx(openIdx === i ? -1 : i)}>
                <span>{it.q}</span>
                {openIdx === i ? <Minus className="w-5 h-5 text-red-600" /> : <Plus className="w-5 h-5 text-red-600" />}
              </div>
              <div className="faq-a">{it.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   FOOTER
========================================================= */
function Footer({ openContact }) {
  return (
    <footer className="bg-black text-white pt-16 pb-8 px-6" data-testid="footer">
      <div className="container-max">
        <div className="grid md:grid-cols-3 gap-10 pb-10 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="8LIMBS Fitness" className="h-14 w-14 object-contain" />
              <div>
                <div className="font-display text-xl font-bold">8LIMBS</div>
                <div className="text-red-500 text-[11px] tracking-[0.3em]">FITNESS</div>
              </div>
            </div>
            <p className="mt-5 text-white/60 text-sm leading-relaxed max-w-xs">
              Muay Thai-style fitness built for real people. Train anywhere. Change everything.
            </p>
          </div>

          <div>
            <div className="section-eyebrow">Navigate</div>
            <ul className="mt-4 space-y-2 text-white/80">
              <li><a href="#program" className="hover:text-red-500 transition-colors">Program</a></li>
              <li><a href="#why" className="hover:text-red-500 transition-colors">Why It Works</a></li>
              <li><a href="#pricing" className="hover:text-red-500 transition-colors">Pricing</a></li>
              <li><a href="#classes" className="hover:text-red-500 transition-colors">Group Classes</a></li>
              <li><a href="#store" className="hover:text-red-500 transition-colors">Fight Wear</a></li>
              <li><a href="#faq" className="hover:text-red-500 transition-colors">FAQ</a></li>
            </ul>
          </div>

          <div>
            <div className="section-eyebrow">Get In Touch</div>
            <ul className="mt-4 space-y-3 text-white/80">
              <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-red-500" /> Cobble Hill · Duncan BC</li>
              <li>
                <a href="mailto:8limbsfitness8@gmail.com" className="flex items-center gap-3 hover:text-red-500 transition-colors" data-testid="footer-email">
                  <Mail className="w-4 h-4 text-red-500" /> 8limbsfitness8@gmail.com
                </a>
              </li>
            </ul>
            <div className="mt-5 flex items-center gap-3">
              <a
                href="https://instagram.com/8limbsfitness8"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram @8limbsfitness8"
                data-testid="footer-instagram"
                className="group flex items-center gap-2 px-4 h-10 border border-white/20 hover:bg-red-600 hover:border-red-600 transition-colors"
              >
                <Instagram className="w-4 h-4" />
                <span className="font-display text-xs tracking-[0.15em] uppercase">@8limbsfitness8</span>
              </a>
            </div>
            <button
              onClick={openContact}
              data-testid="footer-cta"
              className="btn-primary mt-6"
              style={{ padding: "0.7rem 1.4rem", fontSize: "0.8rem" }}
            >
              Book Session
            </button>
          </div>
        </div>

        <div className="pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-white/50 text-sm">
          <div>© {new Date().getFullYear()} 8LIMBS Fitness. All rights reserved.</div>
          <div className="font-display tracking-[0.25em] text-xs uppercase">Train. Grow. Repeat.</div>
        </div>
      </div>
    </footer>
  );
}

/* =========================================================
   CONTACT MODAL
========================================================= */
function ContactModal({ open, onClose, prefillMessage }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    training_type: "in-person",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (open) {
      setStatus(null);
      setErrMsg("");
      // Pre-fill the message if provided (e.g. from Store DM-to-Order)
      if (prefillMessage) {
        setForm((f) => ({ ...f, message: prefillMessage }));
      }
    }
  }, [open, prefillMessage]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", training_type: "in-person", message: "" });
    } catch (err) {
      setStatus("error");
      setErrMsg(
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="contact-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="section-eyebrow">Let's Train</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase text-black">
              Book your session
            </h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="modal-close" className="text-gray-400 hover:text-black">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="divider-red mt-2 mb-6" />

        {status === "success" ? (
          <div className="text-center py-8" data-testid="contact-success">
            <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8" />
            </div>
            <h4 className="font-display text-2xl uppercase text-black">You're in.</h4>
            <p className="text-gray-600 mt-2">I'll be in touch within 24 hours to get you started.</p>
            <button onClick={onClose} className="btn-dark mt-6">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div>
              <label className="block text-xs font-display tracking-[0.2em] uppercase text-gray-600 mb-2">Name *</label>
              <input
                data-testid="contact-name"
                required
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-field"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-xs font-display tracking-[0.2em] uppercase text-gray-600 mb-2">Email *</label>
              <input
                data-testid="contact-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-field"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-display tracking-[0.2em] uppercase text-gray-600 mb-2">Phone</label>
              <input
                data-testid="contact-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input-field"
                placeholder="(optional)"
              />
            </div>
            <div>
              <label className="block text-xs font-display tracking-[0.2em] uppercase text-gray-600 mb-2">Training Type</label>
              <select
                data-testid="contact-type"
                value={form.training_type}
                onChange={(e) => setForm({ ...form, training_type: e.target.value })}
                className="input-field"
              >
                <option value="in-person">In-Person Training</option>
                <option value="online">Online Program</option>
                <option value="not-sure">Not sure yet</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-display tracking-[0.2em] uppercase text-gray-600 mb-2">Message</label>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="input-field"
                placeholder="Tell me about your goals..."
                rows={3}
              />
            </div>

            {status === "error" && (
              <div className="text-red-600 text-sm" data-testid="contact-error">{errMsg}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="contact-submit"
            >
              {loading ? "Sending..." : "Send It"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
            <p className="text-xs text-gray-500 text-center">
              I'll reply within 24 hours. No spam, ever.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   SUCCESS PAGE (Stripe post-payment)
========================================================= */
function SuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", payment_status: null, data: null, err: null, attempts: 0 });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState((s) => ({ ...s, status: "error", err: "Missing session id." }));
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 8;
    const INTERVAL_MS = 2000;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", data, err: null, attempts });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment_status: data.payment_status, data, err: null, attempts });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", payment_status: data.payment_status, data, err: null, attempts });
          return;
        }
        setState({ status: "pending", payment_status: data.payment_status, data, err: null, attempts });
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "error", payment_status: null, data: null, err: "Couldn't verify payment. Please contact us.", attempts });
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search]);

  useEffect(() => {
    document.title = "Payment · 8LIMBS Fitness";
  }, []);

  const { status, data } = state;

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-24" data-testid="success-page">
      <div className="max-w-lg w-full bg-[#111] border border-white/10 p-10 text-center">
        <a href="/" className="inline-block mb-6">
          <img src="/logo.png" alt="8LIMBS Fitness" className="h-16 w-16 mx-auto" />
        </a>

        {status === "checking" || status === "pending" ? (
          <div data-testid="success-pending">
            <Loader2 className="w-12 h-12 text-red-500 animate-spin mx-auto mb-5" />
            <h1 className="font-display text-3xl uppercase">Verifying your payment…</h1>
            <p className="text-white/60 mt-3">Hang tight — this usually takes a few seconds.</p>
          </div>
        ) : status === "paid" ? (
          <div data-testid="success-paid">
            <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-5">
              <Check className="w-8 h-8" />
            </div>
            <div className="text-red-500 text-xs font-display tracking-[0.3em] uppercase">Welcome to 8LIMBS</div>
            <h1 className="font-display text-4xl uppercase mt-2">You're in.</h1>
            {data?.plan_name && (
              <p className="mt-3 text-white/70">
                Plan: <span className="text-white font-semibold">{data.plan_name}</span>
                {data.amount_total ? ` · $${(data.amount_total / 100).toFixed(0)} ${data.currency?.toUpperCase()}` : ""}
              </p>
            )}
            <p className="mt-4 text-white/70 leading-relaxed">
              I'll reach out within 24 hours at the email you used to set up your program and your first check-in. Check your inbox for the Stripe receipt.
            </p>
            <button onClick={() => navigate("/")} className="btn-primary mt-8" data-testid="success-home-btn">
              Back to 8LIMBS
            </button>
          </div>
        ) : status === "expired" ? (
          <div data-testid="success-expired">
            <div className="w-16 h-16 bg-white/10 text-red-500 rounded-full flex items-center justify-center mx-auto mb-5">
              <X className="w-8 h-8" />
            </div>
            <h1 className="font-display text-3xl uppercase">Session expired</h1>
            <p className="text-white/60 mt-3">Your checkout session expired. No charge was made.</p>
            <button onClick={() => navigate("/#pricing")} className="btn-primary mt-8">
              Try Again
            </button>
          </div>
        ) : (
          <div data-testid="success-error">
            <div className="w-16 h-16 bg-white/10 text-red-500 rounded-full flex items-center justify-center mx-auto mb-5">
              <X className="w-8 h-8" />
            </div>
            <h1 className="font-display text-3xl uppercase">We couldn't confirm your payment</h1>
            <p className="text-white/60 mt-3">
              {state.err || "If you completed payment, check your email for the receipt. We'll verify within a few minutes."}
            </p>
            <div className="flex gap-3 justify-center mt-8">
              <button onClick={() => navigate("/")} className="btn-ghost">Back Home</button>
              <a href="mailto:8limbsfitness8@gmail.com" className="btn-primary">Email Us</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/* =========================================================
   HOME PAGE
========================================================= */
function HomePage() {
  const [contactOpen, setContactOpen] = useState(false);
  const [prefillMessage, setPrefillMessage] = useState("");

  // IntersectionObserver for reveal animations
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    document.title = "8LIMBS Fitness — Train Like A Fighter. Built For Real People.";
  }, []);

  const openContact = (msg) => {
    setPrefillMessage(typeof msg === "string" ? msg : "");
    setContactOpen(true);
  };

  return (
    <>
      <Nav openContact={openContact} />
      <Hero openContact={openContact} />
      <Ticker />
      <RelatableHook />
      <WhatThisIs />
      <TwoWaysToTrain openContact={openContact} />
      <RemoveFear />
      <MeetCoach openContact={openContact} />
      <WhyThisWorks />
      <WhatYoullFeel />
      <ForNormalPeople />
      <RealTalk />
      <ResultsVision />
      <Community />
      <Pricing openContact={openContact} />
      <GroupClasses openContact={openContact} />
      <Store openContact={openContact} />
      <FinalCTA openContact={openContact} />
      <FAQ />
      <Footer openContact={openContact} />
      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        prefillMessage={prefillMessage}
      />
    </>
  );
}

/* =========================================================
   APP — Router
========================================================= */
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/success" element={<SuccessPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
